import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const Nangcap = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [transaction, setTransaction] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get verification code from URL params
  const verificationCode = searchParams.get("des");
  const amount = transaction?.amount;

  useEffect(() => {
    const checkTransaction = async () => {
      if (!verificationCode) {
        navigate('/plans');
        return;
      }

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('verification_code', verificationCode)
        .single();

      if (error || !data) {
        toast({
          description: "Giao dịch không hợp lệ hoặc đã hết hạn",
        });
        navigate('/plans');
        return;
      }

      setTransaction(data);
      const expiryTime = new Date(data.expires_at).getTime();
      const now = new Date().getTime();
      setTimeLeft(Math.max(0, Math.floor((expiryTime - now) / 1000)));
      setIsLoading(false);
    };

    checkTransaction();
  }, [verificationCode, navigate, toast]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/plans');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, navigate]);

  useEffect(() => {
    if (!transaction) return;

    // Set up real-time subscription to transactions table
    const channel = supabase
      .channel('transaction_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transactions',
          filter: `verification_code=eq.${verificationCode}`,
        },
        async (payload) => {
          if (payload.new && payload.new.is_confirmed) {
            // Reload transaction data to get payment details
            const { data: updatedTransaction } = await supabase
              .from('transactions')
              .select('*')
              .eq('verification_code', verificationCode)
              .single();
            
            if (updatedTransaction) {
              setTransaction(updatedTransaction);
            }

            toast({
              description: "Thanh toán thành công! Đang chuyển hướng...",
            });
            
            // Wait a moment to show the success message before redirecting
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [transaction, verificationCode, navigate, toast]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <p>Đang tải...</p>
    </div>;
  }

  if (!transaction) return null;

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (transaction?.is_confirmed) {
    return (
      <div className="container max-w-md py-4 sm:py-8 px-2 sm:px-4">
        <Card className="p-6 text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Thanh toán thành công!</h1>
          <p className="mb-2">Mã giao dịch: {transaction.verification_code}</p>
          <p className="mb-2">Số tiền: {transaction.amount.toLocaleString()}đ</p>
          <p className="mb-2">Số tháng: {transaction.months}</p>
          {transaction.payment_date && (
            <p className="mb-2">
              Ngày thanh toán: {new Date(transaction.payment_date).toLocaleDateString('vi-VN')}
            </p>
          )}
          {transaction.payment_gateway && (
            <p className="mb-4">Cổng thanh toán: {transaction.payment_gateway}</p>
          )}
          <Button onClick={() => navigate("/chat")} className="w-full">
            Bắt đầu trò chuyện
          </Button>
        </Card>
      </div>
    );
  }

  const qrCodeUrl = `https://qr.sepay.vn/img?bank=BIDV&acc=96247EOY72&template=compact&amount=${transaction.amount}&des=${transaction.verification_code}`;

  return (
    <div className="container max-w-2xl py-4 sm:py-8 px-2 sm:px-4 lg:ml-[250px] space-y-4 sm:space-y-6">
      <Card className="bg-transparent">
        <CardHeader className="text-center">
          <CardTitle className="text-lg sm:text-xl font-medium text-white">
            Hướng dẫn thanh toán qua chuyển khoản ngân hàng
          </CardTitle>
          <div className="mt-2 text-center">
            <div className="text-sm text-white">
              Gói Pro - {transaction?.months} tháng
            </div>
            <div className="mt-2 text-lg font-semibold text-yellow-400">
              Thời gian còn lại: {formatTime(timeLeft)}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
            {/* QR Code section */}
            <div className="space-y-4">
              <h3 className="text-center font-medium text-white">
                Cách 1: Mở app ngân hàng và quét mã QR hoặc nếu bạn trên điện thoại, hãy chụp màn hình mã QR và đăng tải lên app ngân hàng
              </h3>
              <div className="border rounded-lg p-2 sm:p-4 border-gray-700">
                <img 
                  src={qrCodeUrl}
                  alt="QR Code"
                  className="w-full"
                />
              </div>
            </div>

            {/* Bank Details section */}
            <div className="space-y-4">
              <h3 className="text-center font-medium text-white">
                Cách 2: Chuyển khoản thủ công theo thông tin
              </h3>
              <div className="space-y-4">
                <p className="text-center font-medium mt-2 text-white">Ngân hàng BIDV</p>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white">Chủ tài khoản:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">CAO XUAN AN</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText("CAO XUAN AN");
                          toast({
                            description: "Đã sao chép tên tài khoản",
                          });
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-white">Số tài khoản:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">96247EOY72</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText("96247EOY72");
                          toast({
                            description: "Đã sao chép số tài khoản",
                          });
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-white">Số tiền:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">
                        {transaction.amount.toLocaleString()}đ
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(transaction.amount.toString());
                          toast({
                            description: "Đã sao chép số tiền",
                          });
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-white">Nội dung CK:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{transaction.verification_code}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(transaction.verification_code);
                          toast({
                            description: "Đã sao chép nội dung chuyển khoản",
                          });
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="bg-gray-700" />
                
                <div className="text-sm text-white">
                  <p className="font-medium">Lưu ý:</p>
                  <p>Vui lòng giữ nguyên nội dung {transaction.verification_code} khi chuyển khoản để hệ thống tự xác nhận thanh toán.</p>
                  <p className="mt-2 text-yellow-400">Sau chuyển khoản, gói của bạn sẽ được nâng cấp ngay lập tức trong 1 giây</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Nangcap;
